import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft, Calendar, Clock, MapPin, Users, Video, User, Star,
  Loader2, CheckCircle2, XCircle, AlertTriangle, MessageSquare, Save,
  CalendarClock, History, ExternalLink, UserPlus, UserMinus, HelpCircle,
  Download, FileText, Paperclip, Upload, Trash2, StickyNote, Plus,
  CalendarDays, Shield, Edit2, X
} from "lucide-react";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Progress } from "../../components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { cn } from "../../lib/utils";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  scheduled:   { color: "bg-blue-50 text-blue-700 border-blue-200", icon: Calendar, label: "Scheduled" },
  confirmed:   { color: "bg-blue-50 text-blue-700 border-blue-200", icon: CheckCircle2, label: "Confirmed" },
  in_progress: { color: "bg-amber-50 text-amber-700 border-amber-200", icon: Clock, label: "In Progress" },
  completed:   { color: "bg-green-50 text-green-700 border-green-200", icon: CheckCircle2, label: "Completed" },
  cancelled:   { color: "bg-red-50 text-red-700 border-red-200", icon: XCircle, label: "Cancelled" },
  requested:   { color: "bg-purple-50 text-purple-700 border-purple-200", icon: Shield, label: "Pending Approval" },
};

const StarRating = ({ value, onChange, label }) => (
  <div className="space-y-1">
    <Label className="text-xs">{label}</Label>
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map(s => (
        <button key={s} onClick={() => onChange(s)} className="transition-transform hover:scale-110">
          <Star className={cn("w-5 h-5", s <= value ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30")} />
        </button>
      ))}
      <span className="text-xs text-muted-foreground ml-1">{value}/5</span>
    </div>
  </div>
);

function formatFileSize(bytes) {
  if (!bytes) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
}

const SessionDetailPage = () => {
  const { sessionId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registration, setRegistration] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [feedbackData, setFeedbackData] = useState(null);
  const [history, setHistory] = useState([]);
  const [notes, setNotes] = useState([]);
  const [files, setFiles] = useState([]);
  const [activeTab, setActiveTab] = useState("details");
  const [actionLoading, setActionLoading] = useState("");

  // Notes form
  const [newNote, setNewNote] = useState("");
  const [editingNote, setEditingNote] = useState(null);
  const [editNoteContent, setEditNoteContent] = useState("");

  // Upload state
  const [uploading, setUploading] = useState(false);

  // Feedback form state
  const [fbForm, setFbForm] = useState({ rating: 5, instructor_rating: 5, content_rating: 5, venue_rating: 5, comment: "", would_recommend: true });

  // Reschedule form state
  const [rescheduleForm, setRescheduleForm] = useState({ session_date: "", start_time: "", end_time: "", location: "", reason: "" });

  // Add Attendee state
  const [showAddAttendee, setShowAddAttendee] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [addingAttendees, setAddingAttendees] = useState(false);

  const isAdmin = user?.role === "super_admin" || user?.role === "tenant_admin";
  const base = user?.role === "super_admin" ? "/admin" : user?.role === "tenant_admin" ? "/tenant" : user?.role === "manager" ? "/manager" : "/learner";
  const variant = user?.role === "super_admin" ? "super_admin" : user?.role === "tenant_admin" ? "tenant_admin" : "learner";

  const fetchAll = async () => {
    try {
      const [sRes, fbRes, hRes, nRes, fRes] = await Promise.all([
        api.get(`/sessions/${sessionId}`),
        api.get(`/sessions/${sessionId}/feedback`),
        api.get(`/sessions/${sessionId}/history`),
        api.get(`/sessions/${sessionId}/notes`),
        api.get(`/sessions/${sessionId}/files`),
      ]);
      const s = sRes.data;
      setSession(s);
      setFeedbackData(fbRes.data);
      setHistory(hRes.data || []);
      setNotes(nRes.data || []);
      setFiles(fRes.data || []);
      setRescheduleForm({ session_date: s.session_date, start_time: s.start_time, end_time: s.end_time, location: s.location, reason: "" });

      try {
        const regRes = await api.get(`/sessions/${sessionId}/registrations`);
        setRegistrations(regRes.data || []);
        const myReg = (regRes.data || []).find(r => r.user_id === user?.id && r.status !== "cancelled");
        setRegistration(myReg || null);
      } catch {
        setRegistration(null);
      }

      if (fbRes.data?.my_feedback) {
        setFbForm(fbRes.data.my_feedback);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load session");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [sessionId]); // eslint-disable-line

  const handleRegister = async () => {
    setActionLoading("register");
    try {
      await api.post(`/sessions/${sessionId}/register`);
      toast.success("Successfully booked this session");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to register"); }
    finally { setActionLoading(""); }
  };

  const handleCancelRegistration = async () => {
    if (!window.confirm("Cancel your registration for this session?")) return;
    setActionLoading("cancel");
    try {
      await api.delete(`/sessions/${sessionId}/register`);
      toast.success("Registration cancelled");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to cancel"); }
    finally { setActionLoading(""); }
  };

  const handleSubmitFeedback = async () => {
    setActionLoading("feedback");
    try {
      await api.post(`/sessions/${sessionId}/feedback`, fbForm);
      toast.success("Feedback submitted! Thank you.");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to submit feedback"); }
    finally { setActionLoading(""); }
  };

  const handleReschedule = async () => {
    if (!rescheduleForm.reason.trim()) { toast.error("Please provide a reason for rescheduling"); return; }
    setActionLoading("reschedule");
    try {
      await api.post(`/sessions/${sessionId}/reschedule`, rescheduleForm);
      toast.success("Session rescheduled successfully");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to reschedule"); }
    finally { setActionLoading(""); }
  };

  const handleDownloadICS = async () => {
    setActionLoading("ics");
    try {
      const res = await api.get(`/sessions/${sessionId}/ics`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${session?.title?.replace(/\s+/g, "_") || "session"}.ics`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Calendar file downloaded");
    } catch (e) { toast.error("Failed to download calendar file"); }
    finally { setActionLoading(""); }
  };

  const openAddAttendee = async () => {
    setShowAddAttendee(true);
    setSelectedUserIds([]);
    setUserSearch("");
    fetchAvailableUsers("");
  };

  const fetchAvailableUsers = async (q) => {
    setLoadingUsers(true);
    try {
      const res = await api.get(`/sessions/${sessionId}/available-users?q=${encodeURIComponent(q)}`);
      setAvailableUsers(res.data || []);
    } catch { setAvailableUsers([]); }
    finally { setLoadingUsers(false); }
  };

  const handleAddAttendees = async () => {
    if (selectedUserIds.length === 0) { toast.error("Select at least one person"); return; }
    setAddingAttendees(true);
    try {
      const res = await api.post(`/sessions/${sessionId}/add-attendees`, { user_ids: selectedUserIds });
      toast.success(res.data?.message || "Attendees added");
      setShowAddAttendee(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add attendees"); }
    finally { setAddingAttendees(false); }
  };

  // Notes handlers
  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/sessions/${sessionId}/notes`, { content: newNote.trim() });
      setNewNote("");
      toast.success("Note added");
      const nRes = await api.get(`/sessions/${sessionId}/notes`);
      setNotes(nRes.data || []);
    } catch (e) { toast.error("Failed to add note"); }
  };

  const handleUpdateNote = async (noteId) => {
    if (!editNoteContent.trim()) return;
    try {
      await api.put(`/sessions/${sessionId}/notes/${noteId}`, { content: editNoteContent.trim() });
      setEditingNote(null);
      toast.success("Note updated");
      const nRes = await api.get(`/sessions/${sessionId}/notes`);
      setNotes(nRes.data || []);
    } catch (e) { toast.error("Failed to update note"); }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/sessions/${sessionId}/notes/${noteId}`);
      toast.success("Note deleted");
      const nRes = await api.get(`/sessions/${sessionId}/notes`);
      setNotes(nRes.data || []);
    } catch (e) { toast.error("Failed to delete note"); }
  };

  // File handlers
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File too large (max 25MB)"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await api.post(`/sessions/${sessionId}/files`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`"${file.name}" uploaded`);
      const fRes = await api.get(`/sessions/${sessionId}/files`);
      setFiles(fRes.data || []);
    } catch (e) { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ""; }
  };

  const handleFileDownload = async (fileRecord) => {
    try {
      const res = await api.get(`/sessions/files/${fileRecord.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = fileRecord.original_filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const handleFileDelete = async (fileId) => {
    if (!window.confirm("Delete this file?")) return;
    try {
      await api.delete(`/sessions/${sessionId}/files/${fileId}`);
      toast.success("File deleted");
      const fRes = await api.get(`/sessions/${sessionId}/files`);
      setFiles(fRes.data || []);
    } catch (e) { toast.error("Failed to delete file"); }
  };

  if (loading) return (
    <AdminLayout title="Session Details" variant={variant}>
      <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
    </AdminLayout>
  );

  if (!session) return (
    <AdminLayout title="Session Not Found" variant={variant}>
      <div className="text-center py-16">
        <Calendar className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
        <p className="text-sm text-muted-foreground">Session not found</p>
        <Button variant="outline" size="sm" className="mt-4 text-xs" onClick={() => navigate(`${base}/sessions`)}>Back to Sessions</Button>
      </div>
    </AdminLayout>
  );

  const s = session;
  const sc = statusConfig[s.status] || statusConfig.scheduled;
  const StatusIcon = sc.icon;
  const fillPct = s.capacity > 0 ? Math.round((s.enrolled_count / s.capacity) * 100) : 0;
  const isCompleted = s.status === "completed";
  const isCancelled = s.status === "cancelled";
  const canRegister = !isCancelled && !isCompleted && s.enrolled_count < s.capacity && !registration;
  const canCancel = registration && !isCompleted && !isCancelled;
  const canReschedule = isAdmin && !isCompleted && !isCancelled;
  const canFeedback = isCompleted && !feedbackData?.my_feedback;
  const hasFeedback = feedbackData?.my_feedback;

  const tabs = [
    { id: "details", label: "Overview" },
    { id: "notes", label: `Notes (${notes.length})` },
    { id: "files", label: `Files (${files.length})` },
    { id: "feedback", label: `Feedback${feedbackData?.total ? ` (${feedbackData.total})` : ""}` },
    ...(isAdmin ? [{ id: "attendees", label: `Attendees (${registrations.length})` }] : []),
    ...(canReschedule || history.length > 0 ? [{ id: "schedule", label: "Schedule History" }] : []),
  ];

  return (
    <AdminLayout title={s.title} variant={variant}>
      <div className="space-y-5 max-w-5xl" data-testid="session-detail-page">
        <Button variant="ghost" size="sm" className="text-xs gap-1.5 -ml-2" onClick={() => navigate(-1)} data-testid="back-btn">
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </Button>

        {/* Session Header Card */}
        <Card className="border shadow-none">
          <CardContent className="p-5">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Badge className={cn("text-xs px-2 py-0.5 border gap-1", sc.color)} data-testid="session-status">
                    <StatusIcon className="w-3 h-3" /> {sc.label}
                  </Badge>
                  <Badge variant="outline" className="text-xs">{s.location_type === "virtual" ? "Virtual" : s.location_type === "hybrid" ? "Hybrid" : "In-Person"}</Badge>
                </div>
                <h1 className="text-xl font-bold mb-1" data-testid="session-title">{s.title}</h1>
                {s.description && <p className="text-sm text-muted-foreground">{s.description}</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0 flex-wrap">
                <Button variant="outline" size="sm" onClick={handleDownloadICS} disabled={actionLoading === "ics"} className="gap-1.5 text-xs" data-testid="download-ics-btn">
                  {actionLoading === "ics" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarDays className="w-3.5 h-3.5" />} Add to Calendar
                </Button>
                {canRegister && (
                  <Button onClick={handleRegister} disabled={actionLoading === "register"} className="gap-1.5" data-testid="register-btn">
                    {actionLoading === "register" ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Book Session
                  </Button>
                )}
                {canCancel && (
                  <Button variant="outline" onClick={handleCancelRegistration} disabled={actionLoading === "cancel"} className="gap-1.5 text-red-600 hover:text-red-700 border-red-200" data-testid="cancel-reg-btn">
                    {actionLoading === "cancel" ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserMinus className="w-4 h-4" />} Cancel Booking
                  </Button>
                )}
                {registration && <Badge className="bg-green-50 text-green-700 border-green-200 text-xs gap-1"><CheckCircle2 className="w-3 h-3" /> Registered</Badge>}
              </div>
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-5 pt-5 border-t">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0"><Calendar className="w-4 h-4 text-blue-600" /></div>
                <div><p className="text-[10px] text-muted-foreground">Date</p><p className="text-sm font-medium">{new Date(s.session_date + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}</p></div>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center shrink-0"><Clock className="w-4 h-4 text-amber-600" /></div>
                <div><p className="text-[10px] text-muted-foreground">Time</p><p className="text-sm font-medium">{s.start_time} - {s.end_time}</p></div>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-violet-50 flex items-center justify-center shrink-0">{s.location_type === "virtual" ? <Video className="w-4 h-4 text-violet-600" /> : <MapPin className="w-4 h-4 text-violet-600" />}</div>
                <div><p className="text-[10px] text-muted-foreground">Location</p>
                  <p className="text-sm font-medium">{s.location || "TBD"}</p>
                  {s.virtual_link && <a href={s.virtual_link} target="_blank" rel="noopener noreferrer" className="text-[10px] text-primary flex items-center gap-0.5 hover:underline">Join link <ExternalLink className="w-2.5 h-2.5" /></a>}
                </div>
              </div>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0"><Users className="w-4 h-4 text-emerald-600" /></div>
                <div>
                  <p className="text-[10px] text-muted-foreground">Capacity</p>
                  <p className="text-sm font-medium">{s.enrolled_count}/{s.capacity} enrolled</p>
                  <Progress value={fillPct} className={`h-1 mt-1 w-20 ${fillPct > 85 ? "[&>div]:bg-red-500" : ""}`} />
                </div>
              </div>
            </div>
            {s.instructor_name && (
              <div className="flex items-center gap-2.5 mt-4 pt-4 border-t">
                <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center"><User className="w-4 h-4 text-primary" /></div>
                <div><p className="text-[10px] text-muted-foreground">Instructor</p><p className="text-sm font-medium">{s.instructor_name}</p></div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b overflow-x-auto">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={cn("px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px whitespace-nowrap", activeTab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground")}
              data-testid={`tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── OVERVIEW TAB ── */}
        {activeTab === "details" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <Card className="border shadow-none">
              <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Session Information</CardTitle></CardHeader>
              <CardContent className="px-5 pb-5 space-y-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-muted-foreground text-xs">Session ID</span><p className="font-mono text-xs mt-0.5">{s.id.slice(0, 8)}...</p></div>
                  <div><span className="text-muted-foreground text-xs">Course</span><p className="text-xs mt-0.5">{s.course_id.slice(0, 8)}...</p></div>
                  <div><span className="text-muted-foreground text-xs">Location Type</span><p className="text-xs mt-0.5 capitalize">{s.location_type}</p></div>
                  <div><span className="text-muted-foreground text-xs">Status</span><p className="text-xs mt-0.5 capitalize">{s.status}</p></div>
                </div>
                {isCompleted && feedbackData && (
                  <div className="p-3 rounded-lg bg-green-50 border border-green-100">
                    <div className="flex items-center gap-2">
                      <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                      <span className="text-sm font-bold">{feedbackData.avg_rating}</span>
                      <span className="text-xs text-muted-foreground">average ({feedbackData.total} reviews)</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            <Card className="border shadow-none">
              <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Quick Actions</CardTitle></CardHeader>
              <CardContent className="px-5 pb-5 space-y-2">
                <Button variant="outline" className="w-full justify-start gap-2 text-xs h-9" onClick={handleDownloadICS} data-testid="quick-ics-btn">
                  <Download className="w-4 h-4" /> Download .ICS Calendar File
                </Button>
                {canReschedule && (
                  <Button variant="outline" className="w-full justify-start gap-2 text-xs h-9" onClick={() => setActiveTab("schedule")} data-testid="reschedule-toggle-btn">
                    <CalendarClock className="w-4 h-4" /> Reschedule Session
                  </Button>
                )}
                <Button variant="outline" className="w-full justify-start gap-2 text-xs h-9" onClick={() => setActiveTab("notes")} data-testid="quick-notes-btn">
                  <StickyNote className="w-4 h-4" /> View / Add Notes ({notes.length})
                </Button>
                <Button variant="outline" className="w-full justify-start gap-2 text-xs h-9" onClick={() => setActiveTab("files")} data-testid="quick-files-btn">
                  <Paperclip className="w-4 h-4" /> Uploaded Files ({files.length})
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ── NOTES TAB ── */}
        {activeTab === "notes" && (
          <div className="space-y-4">
            <Card className="border shadow-none">
              <CardHeader className="pt-4 pb-2 px-5">
                <CardTitle className="text-sm flex items-center gap-2"><StickyNote className="w-4 h-4 text-primary" /> Session Notes</CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-5 space-y-4">
                {/* Add note form */}
                <div className="flex gap-2">
                  <Textarea
                    value={newNote}
                    onChange={e => setNewNote(e.target.value)}
                    placeholder="Add a note about this session..."
                    rows={2}
                    className="text-sm flex-1"
                    data-testid="new-note-input"
                  />
                  <Button onClick={handleAddNote} disabled={!newNote.trim()} className="shrink-0 gap-1.5" data-testid="add-note-btn">
                    <Plus className="w-4 h-4" /> Add
                  </Button>
                </div>

                {/* Notes list */}
                {notes.length === 0 ? (
                  <div className="text-center py-8">
                    <StickyNote className="w-8 h-8 mx-auto text-muted-foreground/20 mb-2" />
                    <p className="text-sm text-muted-foreground">No notes yet. Be the first to add one.</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {notes.map(n => (
                      <div key={n.id} className="p-3 rounded-lg border group" data-testid={`note-${n.id}`}>
                        {editingNote === n.id ? (
                          <div className="space-y-2">
                            <Textarea value={editNoteContent} onChange={e => setEditNoteContent(e.target.value)} rows={2} className="text-sm" />
                            <div className="flex gap-2 justify-end">
                              <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => setEditingNote(null)}>Cancel</Button>
                              <Button size="sm" className="text-xs h-7" onClick={() => handleUpdateNote(n.id)}>Save</Button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm whitespace-pre-wrap">{n.content}</p>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-[10px] text-muted-foreground">{n.user_name} &middot; {new Date(n.created_at).toLocaleString()}</span>
                              {(n.user_id === user?.id || isAdmin) && (
                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button className="text-muted-foreground hover:text-primary" onClick={() => { setEditingNote(n.id); setEditNoteContent(n.content); }}>
                                    <Edit2 className="w-3 h-3" />
                                  </button>
                                  <button className="text-muted-foreground hover:text-red-500" onClick={() => handleDeleteNote(n.id)}>
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ── FILES TAB ── */}
        {activeTab === "files" && (
          <div className="space-y-4">
            <Card className="border shadow-none">
              <CardHeader className="pt-4 pb-2 px-5 flex flex-row items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2"><Paperclip className="w-4 h-4 text-primary" /> Session Files</CardTitle>
                <div>
                  <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} data-testid="file-input" />
                  <Button size="sm" className="gap-1.5 text-xs" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-file-btn">
                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    {uploading ? "Uploading..." : "Upload File"}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="px-5 pb-5">
                {files.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-8 h-8 mx-auto text-muted-foreground/20 mb-2" />
                    <p className="text-sm text-muted-foreground">No files uploaded yet</p>
                    <p className="text-[10px] text-muted-foreground mt-1">Upload training materials, handouts, or session resources (max 25MB)</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {files.map(f => (
                      <div key={f.id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/20 group" data-testid={`file-${f.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                            <FileText className="w-4 h-4 text-blue-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{f.original_filename}</p>
                            <p className="text-[10px] text-muted-foreground">{formatFileSize(f.size)} &middot; {f.uploaded_by_name} &middot; {new Date(f.created_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleFileDownload(f)} data-testid={`download-file-${f.id}`}>
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                          {(f.uploaded_by === user?.id || isAdmin) && (
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground hover:text-red-500" onClick={() => handleFileDelete(f.id)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* ── FEEDBACK TAB ── */}
        {activeTab === "feedback" && (
          <div className="space-y-5">
            {canFeedback && (
              <Card className="border-2 border-primary/20 shadow-none">
                <CardHeader className="pt-4 pb-2 px-5">
                  <CardTitle className="text-sm flex items-center gap-2"><MessageSquare className="w-4 h-4 text-primary" /> Share Your Feedback</CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-5 space-y-4">
                  <div className="p-3 rounded-lg bg-blue-50 border border-blue-100 flex items-start gap-2">
                    <HelpCircle className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                    <p className="text-[11px] text-blue-700">Your feedback helps improve future training sessions.</p>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <StarRating label="Overall" value={fbForm.rating} onChange={v => setFbForm({ ...fbForm, rating: v })} />
                    <StarRating label="Instructor" value={fbForm.instructor_rating} onChange={v => setFbForm({ ...fbForm, instructor_rating: v })} />
                    <StarRating label="Content" value={fbForm.content_rating} onChange={v => setFbForm({ ...fbForm, content_rating: v })} />
                    <StarRating label="Venue/Setup" value={fbForm.venue_rating} onChange={v => setFbForm({ ...fbForm, venue_rating: v })} />
                  </div>
                  <div className="space-y-1.5"><Label className="text-xs">Comments</Label><Textarea value={fbForm.comment} onChange={e => setFbForm({ ...fbForm, comment: e.target.value })} rows={3} placeholder="What did you like? What could be improved?" className="text-sm" /></div>
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                      <input type="checkbox" checked={fbForm.would_recommend} onChange={e => setFbForm({ ...fbForm, would_recommend: e.target.checked })} className="rounded border-muted-foreground/30" />
                      I would recommend this session
                    </label>
                    <Button onClick={handleSubmitFeedback} disabled={actionLoading === "feedback"} className="gap-1.5" data-testid="submit-feedback-btn">
                      {actionLoading === "feedback" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Submit Feedback
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {hasFeedback && (
              <Card className="border shadow-none bg-green-50/30">
                <CardContent className="p-5 flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-green-800">You've submitted feedback for this session</p>
                    <p className="text-xs text-green-600">Rating: {feedbackData.my_feedback.rating}/5</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {!isCompleted && !canFeedback && !hasFeedback && (
              <Card className="border shadow-none">
                <CardContent className="p-8 text-center">
                  <MessageSquare className="w-10 h-10 mx-auto text-muted-foreground/20 mb-3" />
                  <p className="text-sm text-muted-foreground">Feedback is available after the session is completed</p>
                </CardContent>
              </Card>
            )}

            {feedbackData?.feedback?.length > 0 && (
              <Card className="border shadow-none">
                <CardHeader className="pt-4 pb-2 px-5 flex flex-row items-center justify-between">
                  <CardTitle className="text-sm">All Feedback ({feedbackData.total})</CardTitle>
                  {feedbackData.avg_rating > 0 && (
                    <div className="flex items-center gap-1.5">
                      <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                      <span className="text-sm font-bold">{feedbackData.avg_rating}</span>
                      <span className="text-xs text-muted-foreground">avg</span>
                    </div>
                  )}
                </CardHeader>
                <CardContent className="px-5 pb-5 space-y-2">
                  {feedbackData.feedback.map((fb, i) => (
                    <div key={i} className="p-3 rounded-lg border">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-medium">{fb.user_name || "Anonymous"}</span>
                        <div className="flex items-center gap-1">{[1,2,3,4,5].map(star => <Star key={star} className={cn("w-3 h-3", star <= fb.rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/20")} />)}</div>
                      </div>
                      {fb.comment && <p className="text-xs text-muted-foreground">{fb.comment}</p>}
                      <div className="flex items-center gap-3 mt-1.5 text-[10px] text-muted-foreground">
                        <span>Instructor: {fb.instructor_rating}/5</span>
                        <span>Content: {fb.content_rating}/5</span>
                        <span>Venue: {fb.venue_rating}/5</span>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* ── ATTENDEES TAB (Admin only) ── */}
        {activeTab === "attendees" && isAdmin && (
          <Card className="border shadow-none" data-testid="attendees-section">
            <CardHeader className="pt-4 pb-2 px-5 flex flex-row items-center justify-between">
              <CardTitle className="text-sm">Registered Attendees</CardTitle>
              <Button size="sm" className="h-8 text-xs gap-1" onClick={openAddAttendee} data-testid="add-attendee-btn">
                <UserPlus className="w-3.5 h-3.5" /> Add Attendee
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <div className="grid grid-cols-12 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-5 py-2.5 border-b bg-muted/30">
                <span className="col-span-4">Name</span><span className="col-span-4">Email</span><span className="col-span-2 text-center">Status</span><span className="col-span-2 text-center">Registered</span>
              </div>
              {registrations.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">No registrations yet</div>
              ) : registrations.map(r => (
                <div key={r.id} className="grid grid-cols-12 items-center px-5 py-3 border-b hover:bg-muted/20 text-sm">
                  <span className="col-span-4 font-medium text-xs">{r.user_name || "Unknown"}</span>
                  <span className="col-span-4 text-xs text-muted-foreground">{r.user_email}</span>
                  <span className="col-span-2 text-center">
                    <Badge className={cn("text-[9px] px-1.5 py-0 border", {
                      "bg-blue-50 text-blue-700 border-blue-200": r.status === "registered",
                      "bg-green-50 text-green-700 border-green-200": r.status === "attended",
                      "bg-red-50 text-red-700 border-red-200": r.status === "absent",
                      "bg-amber-50 text-amber-700 border-amber-200": r.status === "excused",
                    })}>{r.status}</Badge>
                  </span>
                  <span className="col-span-2 text-center text-xs text-muted-foreground">{new Date(r.registered_at).toLocaleDateString()}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Add Attendee Dialog */}
        <Dialog open={showAddAttendee} onOpenChange={setShowAddAttendee}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-[#1e3a5f]" /> Add Attendees</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {session?.tenant_id && (
                <div className="px-3 py-2 rounded-lg bg-blue-50 border border-blue-100 text-[11px] text-blue-700">
                  Showing employees from <span className="font-semibold">{session.tenant_name || session.tenant_id}</span> only
                </div>
              )}
              <Input placeholder="Search by name or email..." value={userSearch} onChange={e => { setUserSearch(e.target.value); fetchAvailableUsers(e.target.value); }}
                className="h-9 text-sm" data-testid="attendee-search" />
              <div className="max-h-[280px] overflow-y-auto border rounded-lg divide-y">
                {loadingUsers ? (
                  <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                ) : availableUsers.length === 0 ? (
                  <div className="text-center py-6 text-xs text-muted-foreground">No matching users found</div>
                ) : availableUsers.map(u => (
                  <label key={u.id} className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 cursor-pointer transition-colors" data-testid={`user-option-${u.id}`}>
                    <Checkbox checked={selectedUserIds.includes(u.id)}
                      onCheckedChange={(checked) => setSelectedUserIds(prev => checked ? [...prev, u.id] : prev.filter(id => id !== u.id))} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{u.full_name || "Unknown"}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{u.email}</p>
                    </div>
                    <Badge variant="outline" className="text-[9px] shrink-0">{u.role}</Badge>
                  </label>
                ))}
              </div>
              {selectedUserIds.length > 0 && (
                <p className="text-xs text-muted-foreground">{selectedUserIds.length} user(s) selected</p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowAddAttendee(false)}>Cancel</Button>
              <Button size="sm" className="text-xs gap-1" onClick={handleAddAttendees} disabled={addingAttendees || selectedUserIds.length === 0} data-testid="confirm-add-attendees">
                {addingAttendees ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />}
                Add {selectedUserIds.length || ""} Attendee{selectedUserIds.length !== 1 ? "s" : ""}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── SCHEDULE/HISTORY TAB ── */}
        {activeTab === "schedule" && (
          <div className="space-y-5">
            {canReschedule && (
              <Card className="border shadow-none">
                <CardHeader className="pt-4 pb-2 px-5">
                  <CardTitle className="text-sm flex items-center gap-2"><CalendarClock className="w-4 h-4 text-primary" /> Reschedule Session</CardTitle>
                </CardHeader>
                <CardContent className="px-5 pb-5 space-y-4">
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-100 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-[11px] text-amber-800">All registered attendees will be notified. A reason is required for audit.</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-1.5"><Label className="text-xs">New Date *</Label><Input type="date" value={rescheduleForm.session_date} onChange={e => setRescheduleForm({ ...rescheduleForm, session_date: e.target.value })} className="h-9 text-sm" data-testid="reschedule-date" /></div>
                    <div className="space-y-1.5"><Label className="text-xs">New Start Time</Label><Input type="time" value={rescheduleForm.start_time} onChange={e => setRescheduleForm({ ...rescheduleForm, start_time: e.target.value })} className="h-9 text-sm" /></div>
                    <div className="space-y-1.5"><Label className="text-xs">New End Time</Label><Input type="time" value={rescheduleForm.end_time} onChange={e => setRescheduleForm({ ...rescheduleForm, end_time: e.target.value })} className="h-9 text-sm" /></div>
                  </div>
                  <div className="space-y-1.5"><Label className="text-xs">Location (optional)</Label><Input value={rescheduleForm.location} onChange={e => setRescheduleForm({ ...rescheduleForm, location: e.target.value })} className="h-9 text-sm" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Reason *</Label><Textarea value={rescheduleForm.reason} onChange={e => setRescheduleForm({ ...rescheduleForm, reason: e.target.value })} rows={2} placeholder="e.g. Instructor unavailable, facility maintenance..." className="text-sm" data-testid="reschedule-reason" /></div>
                  <div className="flex justify-end">
                    <Button onClick={handleReschedule} disabled={actionLoading === "reschedule"} className="gap-1.5" data-testid="confirm-reschedule-btn">
                      {actionLoading === "reschedule" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />} Confirm Reschedule
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="border shadow-none">
              <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm flex items-center gap-2"><History className="w-4 h-4" /> Schedule History</CardTitle></CardHeader>
              <CardContent className="px-5 pb-5">
                {history.length === 0 ? (
                  <p className="text-center text-sm text-muted-foreground py-6">No schedule changes recorded</p>
                ) : (
                  <div className="relative">
                    <div className="absolute left-3 top-0 bottom-0 w-px bg-border" />
                    <div className="space-y-4">
                      {history.map((h, i) => (
                        <div key={i} className="relative pl-8">
                          <div className="absolute left-1.5 top-1 w-3 h-3 rounded-full bg-primary border-2 border-background" />
                          <div className="p-3 rounded-lg border">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-semibold capitalize">{h.action}</span>
                              <span className="text-[10px] text-muted-foreground">{new Date(h.created_at).toLocaleString()}</span>
                            </div>
                            <p className="text-xs text-muted-foreground">{h.old_date} {h.old_time} &rarr; {h.new_date} {h.new_time}</p>
                            {h.reason && <p className="text-xs mt-1">Reason: {h.reason}</p>}
                            <p className="text-[10px] text-muted-foreground mt-1">By {h.changed_by_name}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default SessionDetailPage;
